import type {
  TuituiInteractiveCallbackMessage,
  TuituiEventBody,
  TuituiMessageData,
  TuituiMessageMedia,
} from '@qihoo/tuitui-bot-sdk';
import { CHANNEL_ID } from './const';
import {CHAT_TYPE_DIRECT,CHAT_TYPE_GROUP,CHAT_TYPE_CHANNEL,ChatType,teamsParseChatId,teamsBuildChatId,buildPeerIdFromChatId} from "./chat_base"
import {tuituiEmojiReaction, sendTextMsg, sendMediaMsg } from "./outbound";
import { addUnmentionedHistory, popUnmentionedHistories } from "./histories";
import { StringDeduplicator } from "./deduplicator"
import { appendAuthGuardAlert } from "./cli-guard";
import { handleCardAction } from "./cards";
import { isAllowAccount, isAllowOneOfAccount } from "./accounts";
import { createTuituiClient } from './sdk';
import { getOpenClawConfig, INJECT_CONTEXT_BORDER } from './utils';
import { version as pluginVersion } from '../package.json';
import { BUILD_TIME } from './build_info';

// 会话上下文注入排重
let _session_ctx_injected = new StringDeduplicator(1000, 3600);

/** 子函数共享的可变上下文，子函数直接修改字段，外层读取结果 */
interface ChatPayload {
  chatType:     ChatType;
  chatId:       string | undefined;
  botName:      string;
  text:         string | undefined;
  groupName:    string | undefined;
  channelName:  string | undefined;
  media:         TuituiMessageMedia[];
  replyToId:    string | undefined;  // 引用ID
  msgId?:        string;       // 消息ID
  tuituiAccount:  string;
  vpnuser?:       string;
  tuituiUid:      string | undefined;
  tuituiUserName: string | undefined;
  timestamp:      number | undefined;
  WasMentioned:   boolean | undefined;
}

export interface InboundAccount {
  accountId: string;
  appId: string;
  appSecret: string;
  dmPolicy: string;
  allowFrom: string[];
  groupPolicy: string;
  groupAllowFrom: string[];
  requireMention: boolean;
  emojiReaction: boolean;
}
export interface InboundHandlerOptions {
  body: TuituiEventBody;
  account: InboundAccount;
  apiRuntime: any;
  log: any;
}

interface TuiTuiOutboundDeliverOptions {
  text?: string;
  body?: string;
  mediaUrl?: string;
  mediaUrls?: string[];
}

function getSessionKey(cfg: any, payload: ChatPayload, account: InboundAccount, apiRuntime: any): string {
  const { chatId, chatType } = payload;
  const { accountId } = account;

  if (!chatId) return '';

  const id = buildPeerIdFromChatId(chatId, chatType);

  // sessionKey 不能自己拼字符串，需要用系统api识别，他会根据 bindings 路由规则处理
  const { sessionKey } = apiRuntime.channel.routing.resolveAgentRoute({
    cfg,
    accountId,
    channel: CHANNEL_ID,
    peer: {  kind: chatType, id:    id },
  });
  return String(sessionKey).replace(/\//g, '_');
}

async function dispatchReply(ctx: any, cfg: any, account: InboundAccount, payload: ChatPayload, apiRuntime: any, log: any) {
  const { accountId } = account;
  await apiRuntime.channel.reply.dispatchReplyWithBufferedBlockDispatcher({
    ctx,
    cfg,
    dispatcherOptions: {
      deliver: async (outbound: TuiTuiOutboundDeliverOptions) => {
        // Media message
        const mediaUrl = outbound.mediaUrl || outbound.mediaUrls?.[0];
        if (mediaUrl) {
          await sendMediaMsg(account, payload.chatId, payload.chatType, mediaUrl, [payload.tuituiAccount]);
          return;
        }

        // Text message
        const replyText = outbound?.text || outbound?.body;
        if (replyText) {
          // 群消息回复时不再使用 reference_msgid 避免并发时引用错乱，依靠 @ 来提醒用户
          await sendTextMsg(account, payload.chatId, payload.chatType, replyText);
          return;
        }
        log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, no_reply_content`);
      },

      onReplyStart: () => {
        log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, Agent is typing to ${payload.tuituiAccount}`);
      },
    },
  });
}

/***  配对节流映射 - 用于防止短时间内大量重复配对请求消息发出 */
const pairingDeduplicator = new StringDeduplicator(1000, 120); // 2 分钟内同一个用户只能触发一次配对请求

async function sendSingleChatPairingMsg(account: any, payload: any, log: any, apiRuntime: any) {
  const { accountId } = account;
  const { chatId, chatType } = payload;
  const throttleKey = `${accountId}-${chatId}`;

  if (!pairingDeduplicator.checkAndRecord(throttleKey)) {
    return log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, pairing throttled for sender=${chatId}`);
  }
  try {
    log?.debug?.(`[${CHANNEL_ID}] AccountId: ${accountId}, pairing flow: checking if pairing request exists for sender=${chatId}`);
    const req = await apiRuntime.channel.pairing.upsertPairingRequest({ channel: CHANNEL_ID, accountId, id: chatId });
    log?.debug?.(`[${CHANNEL_ID}] AccountId: ${accountId}, pairing flow: upsertPairingRequest result=${JSON.stringify(req)}`);
    // 只要没配对，会每次和机器人聊，都会返回配对信息。
    const replyText = apiRuntime.channel.pairing.buildPairingReply({ channel: CHANNEL_ID, code: req.code });
    await sendTextMsg(account, chatId, chatType, replyText);
  } catch (err) {
    log?.warn?.(`[${CHANNEL_ID}] AccountId: ${accountId}, pairing failed for ${chatId}: ${String(err)}`);
  }
}

async function isChannelCoordAuthorized(tuituiAccount: string, apiRuntime: any, account: InboundAccount, chat_id:string) {
  // 协调员身份校验
  if (tuituiAccount != "bot-FiwwCeDw" && tuituiAccount != "bot-sUwUeknd") return false;

  // 当前 thread 中有白名单用户发帖，则协调员继承该权限
  const client = createTuituiClient(account);
  const { team_id, channel_id, parent_id } = teamsParseChatId(chat_id);
  const [members, posts] = await Promise.all([
    client.teams.getMembers(team_id),
    client.teams.getPostChainForAgent(team_id, channel_id, parent_id || ""),
  ]);
  if (!members?.length || !posts?.length) return false;

  const from_uids = posts.map((p: any) => p.from_uid).filter(Boolean);
  if (!from_uids.length) return false;

  //console.log("from_uids", from_uids);

  // 将 uid 映射到 user_account
  const from_accounts: string[] = members
    .filter((m: any) => from_uids.includes(m.uid) && m.account)
    .map((m: any) => String(m.account));
  if (!from_accounts.length) return false;

  //console.log("from_accounts", from_accounts);
  return isAllowOneOfAccount(from_accounts, apiRuntime, account);
}


/**
 * 处理单聊（single_chat）、群聊、团队帖子分支，直接修改并校验 payload。
 * 返回 false 表示消息不合法，外层应提前 return；返回 true 表示校验通过，继续执行。
 */
async function parse_single_chat(payload: ChatPayload, msgData: any, account: InboundAccount, apiRuntime: any, log: any): Promise<boolean> {
  const { accountId, dmPolicy, allowFrom } = account;
  const chatId = payload.tuituiAccount ? String(payload.tuituiAccount || '').toLowerCase().trim() : '';
  if (dmPolicy === 'disabled') {  //['pairing', 'allowlist', 'open', 'disabled'],
    log?.warn?.(`[${CHANNEL_ID}] AccountId: ${accountId}, DM disabled sender=${chatId}`);
    return false;
  }

  payload.chatType = CHAT_TYPE_DIRECT;
  payload.chatId   = chatId;
  payload.text     = createTuituiClient(account).event.renderMessageBody(msgData);
  payload.msgId    = msgData.msgid;
  log?.debug?.(
    `[${CHANNEL_ID}] AccountId: ${accountId}, inbound single_chat:
    userAccount=${chatId},
    tuituiUid=${payload.tuituiUid},
    tuituiUserName=${payload.tuituiUserName},
    dmPolicy=${dmPolicy},
    allowFrom=${JSON.stringify(allowFrom)}`);

  if (!chatId && !payload.tuituiUid) {
    log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, Missing user_account or uid in single_chat event`);
    return false;
  }

  if (dmPolicy === 'open') return true;

  if(await isAllowAccount(chatId, apiRuntime, account)){
    return true;
  }

  if (dmPolicy === 'pairing') {
    void sendSingleChatPairingMsg(account, payload, log, apiRuntime);
    log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, pairing required`);
    return false;
  }
  // allowlist
  log?.warn?.(`[${CHANNEL_ID}] AccountId: ${accountId}, Blocked unauthorized sender (allowlist): sender=${chatId} allowFrom=${JSON.stringify(allowFrom)}`);
  return false;
}

async function parse_group_chat(payload: ChatPayload, msgData: any, account: InboundAccount, apiRuntime: any, log: any): Promise<boolean> {
  const { accountId, groupPolicy, groupAllowFrom } = account;
  payload.chatType  = CHAT_TYPE_GROUP;
  const chatId     = msgData.group_id ? String(msgData.group_id).toLowerCase().trim() : '';
  payload.chatId   = chatId;
  payload.groupName = msgData.group_name;
  payload.msgId  = msgData.msgid;
  payload.text = createTuituiClient(account).event.renderMessageBody(msgData);
  payload.WasMentioned = msgData.at_me;
  const { tuituiAccount } = payload;
  log?.debug?.(
    `[${CHANNEL_ID}] AccountId: ${accountId}, inbound group_chat:
    tuituiAccount=${tuituiAccount},
    tuituiUid=${payload.tuituiUid},
    tuituiUserName=${payload.tuituiUserName},
    groupId=${chatId},
    groupPolicy=${groupPolicy},
    groupAllowFrom=${JSON.stringify(groupAllowFrom)},
    at_me=${JSON.stringify(msgData.at_me)},
    at=${JSON.stringify(msgData.at)}`);

  if (groupPolicy === 'disabled') {
    log?.info?.(`${CHANNEL_ID} AccountId: ${accountId}, groupPolicy disabled`);
    return false;
  }

  if (!tuituiAccount || !chatId) {
    log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, Missing tuituiAccount or chatId in group_chat event`);
    return false;
  }

  if (!msgData.at_me && account.requireMention) {
    log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, ignore group message (not mentioned), add to history key: ${chatId}`);
    await addUnmentionedHistory(apiRuntime, accountId, chatId, payload.tuituiUserName, tuituiAccount, payload.text, payload.timestamp);
    return false;
  }

  // 私聊白名单对群聊仍然生效，当群里只想特定人@时，可以不配置groupAllowFrom，而是配置 allowFrom
  if(await isAllowAccount(tuituiAccount, apiRuntime, account)){
    return true;
  }

  if (!groupAllowFrom.includes(chatId)) {
    if (!pairingDeduplicator.checkAndRecord(`${accountId}-${chatId}`)) {
      log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, group pairing throttled for groupId=${chatId}`);
      return false;
    }

    const msgTxt=`当前 OpenClaw（AccountId: ${accountId}）的群聊策略为白名单模式。
需由龙虾主人在配置中添加白名单（两种方式二选一即可）：

- 允许群中所有人用，在群白名单（Group Allow From）添加当前群ID： ${chatId}

- 不想群所有人用，只想特定人用，在私聊白名单(Allow From)添加当前用户 ${payload.tuituiAccount} ，私聊白名单对群仍然有效`

    await sendTextMsg(account, chatId, payload.chatType, msgTxt);
    return false;
  }

  return true;
}


async function parse_teams_post(payload: ChatPayload, msgData: any, account: InboundAccount, apiRuntime: any, log: any): Promise<boolean> {
  const { accountId, groupPolicy, groupAllowFrom } = account;
  payload.chatType  = CHAT_TYPE_CHANNEL;
  const { team_id, channel_id, parent_id, post_id, content, team_name, channel_name } = msgData;
  const thread_id   = (parent_id && parent_id != "0")?parent_id: post_id;
  const chatId      = teamsBuildChatId(team_id, channel_id, thread_id);
  payload.chatId    = chatId;
  payload.msgId     = post_id;
  payload.text      = content;
  payload.channelName = channel_name;
  payload.replyToId = "";
  payload.WasMentioned = msgData.at_me;
  const { tuituiAccount } = payload;
  log?.debug?.(
    `[${CHANNEL_ID}] AccountId: ${accountId}, inbound teams:
    tuituiAccount=${tuituiAccount},
    tuituiUid=${payload.tuituiUid},
    tuituiUserName=${payload.tuituiUserName},
    chatId=${chatId},
    groupPolicy=${groupPolicy},
    groupAllowFrom=${JSON.stringify(groupAllowFrom)},
    at_me=${JSON.stringify(msgData.at_me)},
    at=${JSON.stringify(msgData.at)}`,
  );

  if (groupPolicy === 'disabled') {
    log?.info?.(`${CHANNEL_ID} AccountId: ${accountId}, groupPolicy disabled`);
    return false;
  }

  if (!tuituiAccount) {
    log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, Missing tuituiAccount or chatId in event`);
    return false;
  }


  {
    // 频道帖子文件、附件处理。必须放在正文里
    const {images, files} = msgData;
    if (images) {
      payload.text += "\n\n附录上文提到的图片列表\n"
      for (const image of images) {
        payload.text += image + "\n";
      }
    }
    if (files) {
      payload.text += "\n\n附录上文提到的文件列表\n"
      for (const file of files) {
        payload.text += `[文件] ${file?.name} : ${file?.url} \n`;
      }
    }
  }

  if (!msgData.at_me && account.requireMention) {
    log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, ignore teams post (not mentioned), add to history ${chatId} -> ${payload.text}`);
    await addUnmentionedHistory(apiRuntime, accountId, chatId, payload.tuituiUserName, tuituiAccount, payload.text, payload.timestamp);
    return false;
  }

  // 私聊白名单对团队仍然生效，限制特定人@时，可以不配置groupAllowFrom，而是配置 allowFrom
  if(await isAllowAccount(tuituiAccount, apiRuntime, account)){
    return true;
  }

  if (!groupAllowFrom.includes(String(team_id)) && !groupAllowFrom.includes(String(channel_id)) ) {
    if (!msgData.at_me) {
      // 解决这个case：requireMention=false时，团队里发任意帖子都会回复加白，搞得没法用
      // 要求必须@才触发回复加白
      log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, ignore teams post (not mentioned)`);
      return false;
    }

    // 频道中允许协调员@自己（且帖子里有白名单用户发帖）
    if (await isChannelCoordAuthorized(tuituiAccount, apiRuntime, account, chatId)) {
      return true;
    }

    if (!pairingDeduplicator.checkAndRecord(`${accountId}-${chatId}`)) {
      log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, teams pairing throttled for teamsChatId=${chatId}`);
      return false;
    }

    const msgTxt=`当前 OpenClaw（AccountId: ${accountId}）的群聊/团队策略为白名单模式。
需由龙虾主人在配置中添加白名单（3种方式选一种即可）：

- 允许团队中所有人用，在群白名单（Group Allow From）添加当前团队ID: ${team_id}
- 仅允许当前频道使用，在群白名单（Group Allow From）添加当前频道ID: ${channel_id}
- 仅允许特定人使用，在私聊白名单(Allow From)添加当前用户: ${payload.tuituiAccount} ，私聊白名单对团队仍然有效`

    await sendTextMsg(account, chatId, payload.chatType, msgTxt);
    return false;
  }

  return true;
}

async function parse_interactive_action(payload: ChatPayload, msgData: any, account: InboundAccount, apiRuntime: any, log: any): Promise<boolean> {
  const { accountId } = account;
  const message: TuituiInteractiveCallbackMessage | undefined = msgData?.message;
  if (!message) {
    log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, interactive_action missing message field`);
    return false;
  }

  const convType = message.conversation?.type;
  const operatorAccount = message.user?.account || payload.tuituiAccount;
  if (convType === 'group') {
    payload.chatType = CHAT_TYPE_GROUP;
    payload.chatId = message.conversation?.targeted || '';
  } else {
    payload.chatType = CHAT_TYPE_DIRECT;
    payload.chatId = operatorAccount ? String(operatorAccount).toLowerCase().trim() : '';
  }

  payload.tuituiAccount = operatorAccount || '';
  payload.tuituiUserName = message.user?.name || payload.tuituiUserName;
  payload.msgId = message.msgid || `card-action-${Date.now()}`;

  if (payload.chatType === CHAT_TYPE_GROUP) {
    if (account.groupPolicy === 'disabled') {
      log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, card action rejected: groupPolicy disabled`);
      return false;
    }
    if (!account.groupAllowFrom.includes(payload.chatId || '') &&
        !(await isAllowAccount(payload.tuituiAccount, apiRuntime, account))) {
      log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, card action rejected: operator not in allowlist, operator=${operatorAccount}`);
      return false;
    }
  } else {
    if (account.dmPolicy === 'disabled') {
      log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, card action rejected: dmPolicy disabled`);
      return false;
    }
    if (account.dmPolicy !== 'open' &&
        !(await isAllowAccount(payload.tuituiAccount, apiRuntime, account))) {
      log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, card action rejected: operator not in DM allowlist, operator=${operatorAccount}`);
      return false;
    }
  }

  const commandText = await handleCardAction({
    message,
    operatorAccount: operatorAccount || '',
    chatId: payload.chatId || '',
    chatType: payload.chatType,
    account,
    log,
  });

  if (!commandText) return false;
  payload.text = commandText;
  return true;
}

async function injectSessionContext(payload: ChatPayload, msgData: any, account: InboundAccount): Promise<string> {
  const { accountId } = account;
  // 不限频率强制注入上下文
  let injectedContext = appendAuthGuardAlert(payload.tuituiAccount, payload.vpnuser);

  // 带频控的注入
  if(!_session_ctx_injected.checkAndRecord(accountId + "_" + payload.chatId)) return injectedContext;

  if (payload.chatType === CHAT_TYPE_CHANNEL) {
    const client = createTuituiClient(account);

    // 明确告知当前频道消息是否@了机器人
    if(payload.WasMentioned) {
      injectedContext += `\n\n【你在当前session中的名字叫: \`${payload.botName}\`，当前消息中用户@了你】\n`;
    } else {
      injectedContext += `\n\n【你在当前session中的名字叫: \`${payload.botName}\`】\n`;
    }

    const { team_id, channel_id } = msgData;
    // 公告
    const annnouncement = await client.teams.getAnnouncement(channel_id);
    if(annnouncement) {
      injectedContext += "\n\n【当前公告内容如下】\n";
      injectedContext += "```\n" + annnouncement + "\n```";
    }

    // 成员
    const members = await client.teams.getMemberNames(team_id);
    if(members) {
      injectedContext += "\n\n【当前团队成员清单如下，每行一个成员名。如果需要询问成员、或者发送给成员时，需要用 @名字 的语法；并且@时必须从如下清单中匹配到最接近的，禁止@不存在的成员】\n";
      injectedContext += "```\n" + members + "\n```";
    }

    injectedContext += `\n\n【关键系统指令：频道产物归档】\n如果是用户原始任务要求生成的最终产物文件，必须通过 \`tuitui_bot_file_space_add\` 工具同步上传一份至文件空间中。`;
  }

  injectedContext += `\n\n【关键系统指令：产物交付】\n如果你的最终任务目标是生成文件（pdf/图片/Markdown/等），在文件生成落盘后你当前的任务并没有结束！你必须使用 \`message\` tool 将这些文件二进制作为 attachments 发回到当前 session 中`;

  return injectedContext;
}

/**
 * 处理长链接消息
 * account: 长连接初始化时，对应的Account配置信息
 * accountId: 长连接启动初始化，对应的配置ID
 */
export async function handleInboundMessage({ body, account, apiRuntime, log }: InboundHandlerOptions) {
  const { accountId } = account;
  const wsEvent = body.event;
  const msg = body;
  const msgData = msg.data || {};
  const { ref } = msgData as TuituiMessageData;
  const eventApi = createTuituiClient(account).event;
  const payload: ChatPayload = {
    chatType:     CHAT_TYPE_DIRECT,
    chatId:       undefined,
    botName:      body.bot_name,
    text:         "",
    groupName:    undefined,
    WasMentioned: undefined,
    channelName:  undefined,
    media:         eventApi.getMessageMedia(msgData),
    replyToId:    ref?.is_me && ref?.msgid ? ref.msgid : undefined,
    tuituiAccount: msg.user_account || "",
    vpnuser:       msg.vpnuser,
    tuituiUid:     msg.uid || "",
    tuituiUserName: msg.user_name || "",
    timestamp:     msg.timestamp ? parseInt(msg.timestamp, 10)*1000 : undefined
  };
  let accepted = false;
  switch (wsEvent) {
    case eventApi.SINGLE_CHAT:
      accepted = await parse_single_chat(payload, msgData, account, apiRuntime, log);
      break;
    case eventApi.GROUP_CHAT:
      accepted = await parse_group_chat(payload, msgData, account, apiRuntime, log);
      break;
    case eventApi.TEAMS_POST_CREATE:
      accepted = await parse_teams_post(payload, msgData, account, apiRuntime, log);
      break;
    case eventApi.INTERACTIVE_ACTION:
      accepted = await parse_interactive_action(payload, msgData, account, apiRuntime, log);
      break;
    default:
      log?.info?.(`[${CHANNEL_ID}] ignore unknown event ${wsEvent}`);
      return;
  }
  if (!accepted) return;

  if (!payload.chatId || !payload.msgId) {
    log?.info?.(`[${CHANNEL_ID}] ignore unknown message, missing chatId or msgId`, payload);
    return;
  }

  if (account.emojiReaction && wsEvent !== eventApi.INTERACTIVE_ACTION) {
    // 因为回复较慢，先回复一个表情
    try{
      await tuituiEmojiReaction(account, payload.chatId, payload.chatType, payload.msgId, '收到');
    } catch (err) {
      // 出错不影响后续逻辑
    }
  }

  // 路由判断
  const routeSenderFrom = payload.tuituiAccount || payload.tuituiUid || 'unknown';

  const cfg = getOpenClawConfig(apiRuntime);
  const sessionKey = getSessionKey(cfg, payload, account, apiRuntime);
  log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, dispatching to agent session=${sessionKey}, chatType: ${payload.chatType}, chatId ${payload.chatId}, routeSenderFrom: ${routeSenderFrom}`);

  payload.text = payload.text || '';
  const stripedBody = payload.text.replace(`@${payload.botName}`, '').trim();
  if (stripedBody.startsWith('/')) {
    payload.text = stripedBody;
    if (payload.text === '/infos') {
      const msg = `推推插件版本: ${pluginVersion}\n插件构建时间: ${BUILD_TIME}\nOpenClaw 版本：${apiRuntime.version}`;
      await sendTextMsg(account, payload.chatId, payload.chatType, msg);
      return;
    }
    if (payload.text.startsWith('/new') || payload.text.startsWith('/reset')) {
      // 清理agent上下文时，同时清空历史
      popUnmentionedHistories(accountId, payload.chatId);
    }
  } else {
    // 指令以外的才注入
    const injectedContext = await injectSessionContext(payload, msgData, account);
    if (injectedContext) {
      payload.text += INJECT_CONTEXT_BORDER + injectedContext;
    }
  }

  // 字段意义 参考 openclaw 代码
  // src\auto-reply\reply\inbound-meta.ts
  // buildInboundUserContextPrefix()
  const ctx: any = {
    Body:               payload.text,
    From:               String(routeSenderFrom),
    To:                 CHANNEL_ID,
    SessionKey:         sessionKey,
    AccountId:          accountId,
    OriginatingChannel: CHANNEL_ID,
    OriginatingTo:      payload.chatId,
    ChatType:           payload.chatType!,
    Surface:            CHANNEL_ID,
    Provider:           CHANNEL_ID,
    SenderName:         payload.tuituiUserName,
    SenderId:           payload.tuituiAccount,
    SenderUsername:     payload.tuituiAccount,
    UserAccount:        payload.tuituiAccount,
    Timestamp:          payload.timestamp,
    CommandAuthorized:  true,  // 允许 /new 等内置命令
  };
  if (payload.chatType == CHAT_TYPE_GROUP && payload.chatId) {
    ctx.WasMentioned = payload.WasMentioned;
    ctx.GroupSubject = payload.groupName;
    ctx.GroupId      = payload.chatId;
    ctx.InboundHistory = popUnmentionedHistories(accountId, payload.chatId);
  }
  if (payload.chatType == CHAT_TYPE_CHANNEL) {
    ctx.WasMentioned = payload.WasMentioned;
    ctx.IsForum = true;
    ctx.GroupSubject = payload.channelName;
    ctx.GroupChannel = payload.chatId;
    ctx.InboundHistory = popUnmentionedHistories(accountId, payload.chatId);
  }
  if (payload.msgId)              ctx.MessageSid = payload.msgId;
  if (payload.media.length) {
    ctx.MediaTypes = payload.media.map(([mime]) => mime);
    ctx.MediaUrls = payload.media.map(([, url]) => url);
  }
  if (payload.replyToId)          ctx.ReplyToId  = payload.replyToId;

  log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, handleInboundMessage dispatchReply ctx: ${JSON.stringify(ctx, null, '  ')}`);
  try {
    await dispatchReply(ctx, cfg, account, payload, apiRuntime, log);
  } catch (err) {
    log?.error?.(`[${CHANNEL_ID}] AccountId: ${accountId}, dispatchReply failed:`, err);
  }
}
